import React, { useState } from 'react';
import { BISTPrice, CurrencyPrice, GoldPrice, CryptoPrice, TabType } from '../services/types';
import { useAuth } from '../contexts/AuthContext';
import { fmtTR } from '../utils/format';
import Portfolio from './Portfolio';
import Payments from './Payments';
import BackupManager from './BackupManager';
import ChangePassword from './ChangePassword';
import Watchlist from './Watchlist';
import AdminPanel from './AdminPanel';

interface DashboardProps {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  stocks: BISTPrice[];
  currencies: CurrencyPrice[];
  golds: GoldPrice[];
  cryptos: CryptoPrice[];
}

const Dashboard: React.FC<DashboardProps> = ({
  activeTab,
  setActiveTab,
  stocks,
  currencies,
  golds,
  cryptos,
}) => {
  const { user, logout } = useAuth();
  const [bistSearchQuery, setBistSearchQuery] = useState('');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [sortConfig, setSortConfig] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);

  const handleSort = (key: string) => {
    setSortConfig(prev => prev?.key === key && prev.dir === 'asc' ? { key, dir: 'desc' } : { key, dir: 'asc' });
  };
  const sortIndicator = (key: string) => sortConfig?.key === key ? (sortConfig.dir === 'asc' ? ' ▲' : ' ▼') : '';

  const sortedStocks = React.useMemo(() => {
    const list = stocks.filter(s => s.StockCode.toLowerCase().includes(bistSearchQuery.toLowerCase()));
    if (!sortConfig) return list;
    return [...list].sort((a: any, b: any) => {
      const va = a[sortConfig.key] ?? 0, vb = b[sortConfig.key] ?? 0;
      return sortConfig.dir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
  }, [stocks, bistSearchQuery, sortConfig]);

  const sortedCurrencies = React.useMemo(() => {
    if (!sortConfig) return currencies;
    return [...currencies].sort((a: any, b: any) => {
      const va = a[sortConfig.key] ?? 0, vb = b[sortConfig.key] ?? 0;
      return sortConfig.dir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
  }, [currencies, sortConfig]);

  const sortedGolds = React.useMemo(() => {
    if (!sortConfig) return golds;
    return [...golds].sort((a: any, b: any) => {
      const va = a[sortConfig.key] ?? 0, vb = b[sortConfig.key] ?? 0;
      return sortConfig.dir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
  }, [golds, sortConfig]);

  const sortedCryptos = React.useMemo(() => {
    if (!sortConfig) return cryptos;
    return [...cryptos].sort((a: any, b: any) => {
      const va = a[sortConfig.key] ?? 0, vb = b[sortConfig.key] ?? 0;
      return sortConfig.dir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
  }, [cryptos, sortConfig]);

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', String(darkMode));
  }, [darkMode]);

  // Check if current user is admin
  const isAdmin = user?.role === 'admin';
  const currencyNameMap: Record<string, string> = {
    USD: 'Dolar', EUR: 'Euro', GBP: 'Sterlin', CHF: 'İsviçre Frangı', CAD: 'Kanada Doları',
    RUB: 'Rus Rublesi', AED: 'BAE Dirhemi', AUD: 'Avustralya Doları', DKK: 'Danimarka Kronu',
    SEK: 'İsveç Kronu', NOK: 'Norveç Kronu', JPY: 'Japon Yeni', KWD: 'Kuveyt Dinarı',
    ZAR: 'G. Afrika Randı', ARS: 'Arjantin Pesosu', AZN: 'Azerbaycan Manatı', BGN: 'Bulgar Levası',
    BHD: 'Bahreyn Dinarı', BRL: 'Brezilya Reali', CNY: 'Çin Yuanı', COP: 'Kolombiya Pesosu',
    GEL: 'Gürcistan Larisi', HKD: 'Hong Kong Doları', HUF: 'Macar Forinti', ILS: 'İsrail Şekeli',
    INR: 'Hindistan Rupisi', IQD: 'Irak Dinarı', IRR: 'İran Riyali', JOD: 'Ürdün Dinarı',
    KRW: 'Güney Kore Wonu', KZT: 'Kazak Tengesi', MXN: 'Meksika Pesosu', MYR: 'Malezya Ringgiti',
    NZD: 'Yeni Zelanda Doları', OMR: 'Umman Riyali', PHP: 'Filipinler Pesosu', PKR: 'Pakistan Rupisi',
    PLN: 'Polonya Zlotisi', QAR: 'Katar Riyali', RON: 'Romanya Leyi', SAR: 'S. Arabistan Riyali',
    SGD: 'Singapur Doları', THB: 'Tayland Bahtı', TND: 'Tunus Dinarı', UAH: 'Ukrayna Grivnası',
    ALL: 'Arnavutluk Leki', BAM: 'Bosna Markı', EGP: 'Mısır Lirası', LYD: 'Libya Dinarı',
    MAD: 'Fas Dirhemi', CSK: 'Çek Korunası', SYP: 'Suriye Lirası', TWD: 'Tayvan Doları',
    LBP: 'Lübnan Lirası', RSD: 'Sırbistan Dinarı', TRY: 'Türk Lirası',
  };
  const getCurrencyName = (code: string, name: string) => currencyNameMap[code] || name;
  const tabs = [
    { id: 'bist' as TabType, label: 'BIST', icon: '📊' },
    { id: 'currency' as TabType, label: 'Döviz', icon: '💱' },
    { id: 'gold' as TabType, label: 'Altın', icon: '🥇' },
    { id: 'crypto' as TabType, label: 'Kripto', icon: '₿' },

    { id: 'portfolio' as TabType, label: 'Portföy', icon: '💼' },
    { id: 'payments' as TabType, label: 'Ödemeler', icon: '💳' },
    { id: 'backup' as TabType, label: 'Yedek', icon: '💾' },
    { id: 'changepassword' as TabType, label: 'Şifre', icon: '🔑' },
    ...(isAdmin ? [{ id: 'admin' as TabType, label: 'Admin', icon: '⚙️' }] : [])
  ];

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Finans</h1>
            <p>Hoşgeldin, {user?.name}!</p>
          </div>
          <div className="header-right">
            <button onClick={() => setDarkMode(d => !d)} className="theme-toggle" title="Tema Değiştir">
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button onClick={logout} className="logout-button">
              Çıkış Yap
            </button>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
      </header>
      
      <main className="App-main">
        {/* BIST Tab */}
        {activeTab === 'bist' && (
          <div className="tab-content">
            {/* Takip Edilen Hisseler Bölümü - Üst Kısım */}
            <div className="watchlist-section" style={{ marginBottom: '20px' }}>
              <Watchlist stocks={stocks} currencies={currencies} golds={golds} cryptos={cryptos} />
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <h3>BIST 100</h3>
                <p className="stat-value">
                  {fmtTR(stocks.find(s => s.StockCode === 'XU100')?.LastTransactionPrice, 0) || 'Yükleniyor...'}
                </p>
                <span className={`stat-change ${(stocks.find(s => s.StockCode === 'XU100')?.ChangePercent || 0) > 0 ? 'positive' : 'negative'}`}>
                  {(() => {
                    const bist100 = stocks.find(s => s.StockCode === 'XU100');
                    return bist100?.ChangePercent ? 
                      (bist100.ChangePercent > 0 ? '+' : '') + 
                      fmtTR(bist100.ChangePercent) + '%' : '0,00%';
                  })()}
                </span>
              </div>
              <div className="stat-card">
                <h3>BIST 30</h3>
                <p className="stat-value">
                  {fmtTR(stocks.find(s => s.StockCode === 'XU30')?.LastTransactionPrice, 0) || 'Yükleniyor...'}
                </p>
                <span className={`stat-change ${(stocks.find(s => s.StockCode === 'XU30')?.ChangePercent || 0) > 0 ? 'positive' : 'negative'}`}>
                  {(() => {
                    const bist30 = stocks.find(s => s.StockCode === 'XU30');
                    return bist30?.ChangePercent ? 
                      (bist30.ChangePercent > 0 ? '+' : '') + 
                      fmtTR(bist30.ChangePercent) + '%' : '0,00%';
                  })()}
                </span>
              </div>
              <div className="stat-card">
                <h3>En Çok Artan</h3>
                <p className="stat-value">
                  {(() => {
                    const topGainer = stocks.filter(s => s.ChangePercent && s.StockCode !== 'XU100' && s.StockCode !== 'XU30')
                      .sort((a, b) => (b.ChangePercent || 0) - (a.ChangePercent || 0))[0];
                    return topGainer ? topGainer.StockCode : 'Yükleniyor...';
                  })()}
                </p>
                <span className="stat-change positive">
                  {(() => {
                    const topGainer = stocks.filter(s => s.ChangePercent && s.StockCode !== 'XU100' && s.StockCode !== 'XU30')
                      .sort((a, b) => (b.ChangePercent || 0) - (a.ChangePercent || 0))[0];
                    return topGainer ? `+${fmtTR(topGainer.ChangePercent)}%` : '+0,00%';
                  })()}
                </span>
              </div>
              <div className="stat-card">
                <h3>En Çok Düşen</h3>
                <p className="stat-value">
                  {(() => {
                    const topLoser = stocks.filter(s => s.ChangePercent && s.StockCode !== 'XU100' && s.StockCode !== 'XU30')
                      .sort((a, b) => (a.ChangePercent || 0) - (b.ChangePercent || 0))[0];
                    return topLoser ? topLoser.StockCode : 'Yükleniyor...';
                  })()}
                </p>
                <span className="stat-change negative">
                  {(() => {
                    const topLoser = stocks.filter(s => s.ChangePercent && s.StockCode !== 'XU100' && s.StockCode !== 'XU30')
                      .sort((a, b) => (a.ChangePercent || 0) - (b.ChangePercent || 0))[0];
                    return topLoser ? `${fmtTR(topLoser.ChangePercent)}%` : '0,00%';
                  })()}
                </span>
              </div>
            </div>

            <div className="stocks-container">
              <div className="stocks-header">
                <h2>Tüm BIST Hisse Senetleri</h2>
                <div className="stock-search-bar">
                  <input
                    type="text"
                    placeholder="Hisse ara... (örn: THYAO)"
                    value={bistSearchQuery}
                    onChange={(e) => setBistSearchQuery(e.target.value)}
                    className="stock-search-input"
                  />
                  {bistSearchQuery && (
                    <button
                      className="stock-search-clear"
                      onClick={() => setBistSearchQuery('')}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="stocks-table">
                <table>
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('StockCode')}>Sembol{sortIndicator('StockCode')}</th>
                      <th className="sortable" onClick={() => handleSort('LastTransactionPrice')}>Son Fiyat{sortIndicator('LastTransactionPrice')}</th>
                      <th className="sortable" onClick={() => handleSort('Change')}>Değişim{sortIndicator('Change')}</th>
                      <th className="sortable" onClick={() => handleSort('ChangePercent')}>% Değişim{sortIndicator('ChangePercent')}</th>
                      <th>Saat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStocks.map((stock) => (
                      <tr key={stock.StockCode}>
                        <td className="stock-symbol">{stock.StockCode}</td>
                        <td className="stock-price">₺{fmtTR(stock.LastTransactionPrice)}</td>
                        <td className={`stock-change ${stock.Change && stock.Change > 0 ? 'positive' : 'negative'}`}>
                          {stock.Change && stock.Change > 0 ? '+' : ''}{fmtTR(stock.Change)}
                        </td>
                        <td className={`stock-change ${stock.ChangePercent && stock.ChangePercent > 0 ? 'positive' : 'negative'}`}>
                          {stock.ChangePercent && stock.ChangePercent > 0 ? '+' : ''}{fmtTR(stock.ChangePercent)}%
                        </td>
                        <td className="stock-time">
                          {new Date(stock.PriceDate).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Döviz Tab */}
        {activeTab === 'currency' && (
          <div className="tab-content">
            <div className="stats-container">
              <div className="stat-card">
                <h3>USD/TL</h3>
                <p className="stat-value">₺{fmtTR(currencies.find(c => c.Code === 'USD')?.BuyPrice) || 'Yükleniyor...'}</p>
                <span className={`stat-change ${currencies.find(c => c.Code === 'USD')?.ChangePercent !== undefined ? (currencies.find(c => c.Code === 'USD')!.ChangePercent > 0 ? 'positive' : 'negative') : (currencies.find(c => c.Code === 'USD')!.Change > 0 ? 'positive' : 'negative')}`}>
                  {currencies.find(c => c.Code === 'USD')?.ChangePercent !== undefined
                    ? ((currencies.find(c => c.Code === 'USD')!.ChangePercent > 0 ? '+' : '') + fmtTR(currencies.find(c => c.Code === 'USD')!.ChangePercent) + '%')
                    : (currencies.find(c => c.Code === 'USD')?.Change ? (currencies.find(c => c.Code === 'USD')!.Change > 0 ? '+' : '') + fmtTR(currencies.find(c => c.Code === 'USD')!.Change) + ' TL' : '0,00 TL')}
                </span>
              </div>
              <div className="stat-card">
                <h3>EUR/TL</h3>
                <p className="stat-value">₺{fmtTR(currencies.find(c => c.Code === 'EUR')?.BuyPrice) || 'Yükleniyor...'}</p>
                <span className={`stat-change ${currencies.find(c => c.Code === 'EUR')?.ChangePercent !== undefined ? (currencies.find(c => c.Code === 'EUR')!.ChangePercent > 0 ? 'positive' : 'negative') : (currencies.find(c => c.Code === 'EUR')!.Change > 0 ? 'positive' : 'negative')}`}>
                  {currencies.find(c => c.Code === 'EUR')?.ChangePercent !== undefined
                    ? ((currencies.find(c => c.Code === 'EUR')!.ChangePercent > 0 ? '+' : '') + fmtTR(currencies.find(c => c.Code === 'EUR')!.ChangePercent) + '%')
                    : (currencies.find(c => c.Code === 'EUR')?.Change ? (currencies.find(c => c.Code === 'EUR')!.Change > 0 ? '+' : '') + fmtTR(currencies.find(c => c.Code === 'EUR')!.Change) + ' TL' : '0,00 TL')}
                </span>
              </div>
              <div className="stat-card">
                <h3>GBP/TL</h3>
                <p className="stat-value">₺{fmtTR(currencies.find(c => c.Code === 'GBP')?.BuyPrice) || 'Yükleniyor...'}</p>
                <span className={`stat-change ${currencies.find(c => c.Code === 'GBP')?.ChangePercent !== undefined ? (currencies.find(c => c.Code === 'GBP')!.ChangePercent > 0 ? 'positive' : 'negative') : (currencies.find(c => c.Code === 'GBP')!.Change > 0 ? 'positive' : 'negative')}`}>
                  {currencies.find(c => c.Code === 'GBP')?.ChangePercent !== undefined
                    ? ((currencies.find(c => c.Code === 'GBP')!.ChangePercent > 0 ? '+' : '') + fmtTR(currencies.find(c => c.Code === 'GBP')!.ChangePercent) + '%')
                    : (currencies.find(c => c.Code === 'GBP')?.Change ? (currencies.find(c => c.Code === 'GBP')!.Change > 0 ? '+' : '') + fmtTR(currencies.find(c => c.Code === 'GBP')!.Change) + ' TL' : '0,00 TL')}
                </span>
              </div>
              <div className="stat-card">
                <h3>Çeşit</h3>
                <p className="stat-value">{currencies.length}</p>
                <span className="stat-change">Döviz çeşidi</span>
              </div>
            </div>

            <div className="stocks-container">
              <h2>Döviz Kurları</h2>
              <div className="stocks-table">
                <table>
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('Code')}>Kur{sortIndicator('Code')}</th>
                      <th>Adı</th>
                      <th className="sortable" onClick={() => handleSort('BuyPrice')}>Alış{sortIndicator('BuyPrice')}</th>
                      <th className="sortable" onClick={() => handleSort('SellPrice')}>Satış{sortIndicator('SellPrice')}</th>
                      <th className="sortable" onClick={() => handleSort('Change')}>Değişim{sortIndicator('Change')}</th>
                      <th className="sortable" onClick={() => handleSort('ChangePercent')}>% Değişim{sortIndicator('ChangePercent')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCurrencies.map((currency) => (
                      <tr key={currency.Code}>
                        <td className="stock-symbol">{currency.Code}</td>
                        <td className="stock-name">{getCurrencyName(currency.Code, currency.Name)}</td>
                        <td className="stock-price">₺{fmtTR(currency.BuyPrice) || '0,00'}</td>
                        <td className="stock-price">₺{fmtTR(currency.SellPrice) || '0,00'}</td>
                        <td className={`stock-change ${currency.Change && currency.Change > 0 ? 'positive' : 'negative'}`}>
                          {currency.Change ? (currency.Change > 0 ? '+' : '') + fmtTR(currency.Change) : '0,00'}
                        </td>
                        <td className={`stock-change ${currency.ChangePercent && currency.ChangePercent > 0 ? 'positive' : 'negative'}`}>
                          {currency.ChangePercent ? (currency.ChangePercent > 0 ? '+' : '') + fmtTR(currency.ChangePercent) + '%' : '0,00%'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Altın Tab */}
        {activeTab === 'gold' && (
          <div className="tab-content">
            <div className="stats-container">
              <div className="stat-card">
                <h3>Çeyrek Altın</h3>
                <p className="stat-value">₺{fmtTR(golds.find(g => g.Type === 'Çeyrek Altın')?.Price) || 'Yükleniyor...'}</p>
                <span className={`stat-change ${golds.find(g => g.Type === 'Çeyrek Altın')?.ChangePercent ? (golds.find(g => g.Type === 'Çeyrek Altın')!.ChangePercent > 0 ? 'positive' : 'negative') : (golds.find(g => g.Type === 'Çeyrek Altın')!.Change > 0 ? 'positive' : 'negative')}`}>
                  {golds.find(g => g.Type === 'Çeyrek Altın')?.ChangePercent
                    ? ((golds.find(g => g.Type === 'Çeyrek Altın')!.ChangePercent > 0 ? '+' : '') + fmtTR(golds.find(g => g.Type === 'Çeyrek Altın')!.ChangePercent) + '%')
                    : (golds.find(g => g.Type === 'Çeyrek Altın')?.Change ? (golds.find(g => g.Type === 'Çeyrek Altın')!.Change > 0 ? '+' : '') + fmtTR(golds.find(g => g.Type === 'Çeyrek Altın')!.Change) + ' TL' : '0,00 TL')}
                </span>
              </div>
              <div className="stat-card">
                <h3>Gram Altın</h3>
                <p className="stat-value">₺{fmtTR(golds.find(g => g.Type === 'Gram Altın')?.Price) || 'Yükleniyor...'}</p>
                <span className={`stat-change ${golds.find(g => g.Type === 'Gram Altın')?.ChangePercent ? (golds.find(g => g.Type === 'Gram Altın')!.ChangePercent > 0 ? 'positive' : 'negative') : (golds.find(g => g.Type === 'Gram Altın')!.Change > 0 ? 'positive' : 'negative')}`}>
                  {golds.find(g => g.Type === 'Gram Altın')?.ChangePercent
                    ? ((golds.find(g => g.Type === 'Gram Altın')!.ChangePercent > 0 ? '+' : '') + fmtTR(golds.find(g => g.Type === 'Gram Altın')!.ChangePercent) + '%')
                    : (golds.find(g => g.Type === 'Gram Altın')?.Change ? (golds.find(g => g.Type === 'Gram Altın')!.Change > 0 ? '+' : '') + fmtTR(golds.find(g => g.Type === 'Gram Altın')!.Change) + ' TL' : '0,00 TL')}
                </span>
              </div>
              <div className="stat-card">
                <h3>Tam Altın</h3>
                <p className="stat-value">₺{fmtTR(golds.find(g => g.Type === 'Tam Altın')?.Price) || 'Yükleniyor...'}</p>
                <span className={`stat-change ${golds.find(g => g.Type === 'Tam Altın')?.ChangePercent ? (golds.find(g => g.Type === 'Tam Altın')!.ChangePercent > 0 ? 'positive' : 'negative') : (golds.find(g => g.Type === 'Tam Altın')!.Change > 0 ? 'positive' : 'negative')}`}>
                  {golds.find(g => g.Type === 'Tam Altın')?.ChangePercent
                    ? ((golds.find(g => g.Type === 'Tam Altın')!.ChangePercent > 0 ? '+' : '') + fmtTR(golds.find(g => g.Type === 'Tam Altın')!.ChangePercent) + '%')
                    : (golds.find(g => g.Type === 'Tam Altın')?.Change ? (golds.find(g => g.Type === 'Tam Altın')!.Change > 0 ? '+' : '') + fmtTR(golds.find(g => g.Type === 'Tam Altın')!.Change) + ' TL' : '0,00 TL')}
                </span>
              </div>
              <div className="stat-card">
                <h3>Çeşit</h3>
                <p className="stat-value">{golds.length}</p>
                <span className="stat-change">Altın çeşidi</span>
              </div>
            </div>

            <div className="stocks-container">
              <h2>Altın Fiyatları</h2>
              <div className="stocks-table">
                <table>
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('Type')}>Tür {sortIndicator('Type')}</th>
                      <th>Birim</th>
                      <th className="sortable" onClick={() => handleSort('Price')}>Fiyat {sortIndicator('Price')}</th>
                      <th className="sortable" onClick={() => handleSort('Change')}>Değişim {sortIndicator('Change')}</th>
                      <th className="sortable" onClick={() => handleSort('ChangePercent')}>% Değişim {sortIndicator('ChangePercent')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedGolds.map((gold) => (
                      <tr key={gold.Type}>
                        <td className="stock-symbol">{gold.Type}</td>
                        <td className="stock-name">{gold.Unit}</td>
                        <td className="stock-price">₺{fmtTR(gold.Price) || '0,00'}</td>
                        <td className={`stock-change ${gold.Change && gold.Change > 0 ? 'positive' : 'negative'}`}>
                          {gold.Change ? (gold.Change > 0 ? '+' : '') + fmtTR(gold.Change) : '0,00'}
                        </td>
                        <td className={`stock-change ${gold.ChangePercent && gold.ChangePercent > 0 ? 'positive' : 'negative'}`}>
                          {gold.ChangePercent ? (gold.ChangePercent > 0 ? '+' : '') + fmtTR(gold.ChangePercent) + '%' : '0,00%'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Kripto Tab */}
        {activeTab === 'crypto' && (
          <div className="tab-content">
            <div className="stats-container">
              <div className="stat-card">
                <h3>Bitcoin</h3>
                <p className="stat-value">${cryptos.find(c => c.Symbol === 'BTC')?.Price?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '—'}</p>
                <span className={`stat-change ${(cryptos.find(c => c.Symbol === 'BTC')?.ChangePercent || 0) > 0 ? 'positive' : 'negative'}`}>
                  {(cryptos.find(c => c.Symbol === 'BTC')?.ChangePercent || 0) > 0 ? '+' : ''}{cryptos.find(c => c.Symbol === 'BTC')?.ChangePercent?.toFixed(2) || '0.00'}%
                </span>
              </div>
              <div className="stat-card">
                <h3>Ethereum</h3>
                <p className="stat-value">${cryptos.find(c => c.Symbol === 'ETH')?.Price?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '—'}</p>
                <span className={`stat-change ${(cryptos.find(c => c.Symbol === 'ETH')?.ChangePercent || 0) > 0 ? 'positive' : 'negative'}`}>
                  {(cryptos.find(c => c.Symbol === 'ETH')?.ChangePercent || 0) > 0 ? '+' : ''}{cryptos.find(c => c.Symbol === 'ETH')?.ChangePercent?.toFixed(2) || '0.00'}%
                </span>
              </div>
              <div className="stat-card">
                <h3>Solana</h3>
                <p className="stat-value">${cryptos.find(c => c.Symbol === 'SOL')?.Price?.toLocaleString('en-US', { maximumFractionDigits: 2 }) || '—'}</p>
                <span className={`stat-change ${(cryptos.find(c => c.Symbol === 'SOL')?.ChangePercent || 0) > 0 ? 'positive' : 'negative'}`}>
                  {(cryptos.find(c => c.Symbol === 'SOL')?.ChangePercent || 0) > 0 ? '+' : ''}{cryptos.find(c => c.Symbol === 'SOL')?.ChangePercent?.toFixed(2) || '0.00'}%
                </span>
              </div>
              <div className="stat-card">
                <h3>Çeşit</h3>
                <p className="stat-value">{cryptos.length}</p>
                <span className="stat-change">Kripto para</span>
              </div>
            </div>

            <div className="stocks-container">
              <h2>Kripto Paralar</h2>
              <div className="stocks-table">
                <table>
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('Symbol')}>Sembol {sortIndicator('Symbol')}</th>
                      <th className="sortable" onClick={() => handleSort('Name')}>Adı {sortIndicator('Name')}</th>
                      <th className="sortable" onClick={() => handleSort('Price')}>Fiyat {sortIndicator('Price')}</th>
                      <th className="sortable" onClick={() => handleSort('Change')}>Değişim {sortIndicator('Change')}</th>
                      <th className="sortable" onClick={() => handleSort('ChangePercent')}>% Değişim {sortIndicator('ChangePercent')}</th>
                      <th className="sortable" onClick={() => handleSort('Volume')}>Hacim {sortIndicator('Volume')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCryptos.map((crypto) => (
                      <tr key={crypto.Symbol}>
                        <td className="stock-symbol">{crypto.Symbol}</td>
                        <td className="stock-name">{crypto.Name}</td>
                        <td className="stock-price">${crypto.Price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: crypto.Price < 1 ? 6 : 2 }) || '0'}</td>
                        <td className={`stock-change ${crypto.Change && crypto.Change > 0 ? 'positive' : 'negative'}`}>
                          {crypto.Change ? (crypto.Change > 0 ? '+$' : '-$') + Math.abs(crypto.Change).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '$0.00'}
                        </td>
                        <td className={`stock-change ${crypto.ChangePercent && crypto.ChangePercent > 0 ? 'positive' : 'negative'}`}>
                          {crypto.ChangePercent ? (crypto.ChangePercent > 0 ? '+' : '') + crypto.ChangePercent.toFixed(2) + '%' : '0.00%'}
                        </td>
                        <td className="stock-volume">{typeof crypto.Volume === 'number' ? '$' + crypto.Volume.toLocaleString('en-US', { maximumFractionDigits: 0 }) : (crypto.Volume || 'N/A')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}



        {/* Portfolio Tab */}
        {activeTab === 'portfolio' && (
          <div className="tab-content">
            <Portfolio
              stocks={stocks}
              currencies={currencies}
              golds={golds}
              cryptos={cryptos}
            />
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="tab-content">
            <Payments />
          </div>
        )}

        {/* Backup Tab */}
        {activeTab === 'backup' && (
          <div className="tab-content">
            <BackupManager />
          </div>
        )}

        {/* Change Password Tab */}
        {activeTab === 'changepassword' && (
          <div className="tab-content">
            <ChangePassword />
          </div>
        )}

        {/* Admin Panel Tab - Only for admin users */}
        {activeTab === 'admin' && isAdmin && (
          <div className="tab-content">
            <AdminPanel />
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
